import { useState, useEffect } from 'react';
import { useI18n } from '../../lib/i18n';
import Modal from '../ui/Modal';
import { Button } from '../ui/Button';
import { useLabels } from '../../lib/crm';
import type { AiAgentDoc, AiRoutingRule, RoutingRuleType } from '../../lib/aiAgents';

interface RoutingRuleModalProps {
  open: boolean;
  agents: AiAgentDoc[];
  onClose: () => void;
  onSaveRule: (rule: AiRoutingRule) => void;
}

export function RoutingRuleModal({
  open,
  agents,
  onClose,
  onSaveRule,
}: RoutingRuleModalProps) {
  const { t } = useI18n();
  const { labels } = useLabels();

  const [type, setType] = useState<RoutingRuleType>('keyword');
  const [matchType, setMatchType] = useState<'exact' | 'contains'>('contains');
  const [value, setValue] = useState('');
  const [agentId, setAgentId] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setType('keyword');
      setMatchType('contains');
      setValue('');
      setAgentId(agents[0]?.id || '');
      setError(null);
    }
  }, [open, agents]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!agentId) {
      setError(t('ai.routing.targetAgentRequired'));
      return;
    }
    if (!value.trim()) {
      setError(t('ai.routing.valueRequired'));
      return;
    }

    const newRule: AiRoutingRule = {
      id: `rule_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      type,
      matchType: type === 'keyword' ? matchType : undefined,
      value: value.trim(),
      agentId,
    };

    onSaveRule(newRule);
    onClose();
  };

  return (
    <Modal open={open} onClose={onClose} title={t('ai.routing.addRule')}>
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="rounded-lg bg-danger-50 p-3 text-xs text-danger dark:bg-rose-950/40 dark:text-rose-400">
            {error}
          </div>
        )}

        <div>
          <label className="block text-xs font-semibold text-ink-2 dark:text-slate-300">
            {t('ai.routing.ruleType')}
          </label>
          <div className="mt-2 grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => {
                setType('keyword');
                setValue('');
              }}
              className={`rounded-lg border p-2.5 text-center text-xs font-semibold transition ${
                type === 'keyword'
                  ? 'border-brand bg-brand-50 text-brand dark:bg-brand-950/40 dark:text-brand-tint'
                  : 'border-line text-ink hover:bg-slate-50 dark:border-slate-700 dark:text-white dark:hover:bg-slate-800'
              }`}
            >
              {t('ai.routing.matchKeyword')}
            </button>
            <button
              type="button"
              onClick={() => {
                setType('label');
                setValue(labels[0]?.id || '');
              }}
              className={`rounded-lg border p-2.5 text-center text-xs font-semibold transition ${
                type === 'label'
                  ? 'border-brand bg-brand-50 text-brand dark:bg-brand-950/40 dark:text-brand-tint'
                  : 'border-line text-ink hover:bg-slate-50 dark:border-slate-700 dark:text-white dark:hover:bg-slate-800'
              }`}
            >
              {t('ai.routing.matchLabel')}
            </button>
          </div>
        </div>

        {type === 'keyword' ? (
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-semibold text-ink-2 dark:text-slate-300">
                {t('ai.routing.matchCondition')}
              </label>
              <select
                value={matchType}
                onChange={(e) => setMatchType(e.target.value as 'exact' | 'contains')}
                className="mt-1 w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-800 dark:text-white"
              >
                <option value="contains">{t('ai.routing.matchContains')}</option>
                <option value="exact">{t('ai.routing.matchExact')}</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-semibold text-ink-2 dark:text-slate-300">
                {t('ai.routing.keywordText')} *
              </label>
              <input
                type="text"
                required
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder={t('ai.routing.keywordsPlaceholder') || 'e.g. price, refund, speak to agent'}
                className="mt-1 w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-800 dark:text-white"
              />
            </div>
          </div>
        ) : (
          <div>
            <label className="block text-xs font-semibold text-ink-2 dark:text-slate-300">
              {t('ai.routing.selectLabel')} *
            </label>
            {labels.length === 0 ? (
              <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
                {t('ai.routing.noLabelsFound')}
              </p>
            ) : (
              <select
                value={value}
                onChange={(e) => setValue(e.target.value)}
                className="mt-1 w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-800 dark:text-white"
              >
                {labels.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.name}
                  </option>
                ))}
              </select>
            )}
          </div>
        )}

        <div>
          <label className="block text-xs font-semibold text-ink-2 dark:text-slate-300">
            {t('ai.routing.targetAgent')} *
          </label>
          <select
            value={agentId}
            onChange={(e) => setAgentId(e.target.value)}
            className="mt-1 w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-800 dark:text-white"
          >
            {agents.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name} (@{a.slug})
              </option>
            ))}
          </select>
        </div>

        <div className="flex justify-end gap-2 pt-3 border-t border-line dark:border-slate-700">
          <Button type="button" variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" variant="primary">
            {t('ai.routing.addRule')}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
